<template>
  <div class="product__combo-no-preview-options-wrapper">
    <template v-if="productOptionChoices.length">
      <div
        :class="index == 0 ? 'mt-0' : ''"
        class="product__option"
        v-for="(productOption, index) of productOptionChoices"
        :key="productOption.name"
      >
        <div>
          <h2>
            {{ productOption.name }}
            <template
              v-if="
                isColorOption(productOption.type) ||
                (canShowOptionAsIcon && isStyleOption(productOption.type))
              "
              >:
              <span class="product__option-value">
                {{ getSelectedOption(productOption.name) }}
              </span>
            </template>
          </h2>

          <div
            class="product__option-choices"
            :class="{
              'product__option-choices--small': isSizeOption(
                productOption.type
              ),
            }"
          >
            <!-- show dropdown list when the number of option is higher than x -->
            <template
              v-if="
                ((productOption.values?.length || 0) >=
                  NUMBER_OF_OPTIONS_TO_SHOW_DROPDOWN &&
                  !isColorOption(productOption.type)) ||
                (isColorOption(productOption.type) && isDropdownColorOption())
              "
            >
              <sib-select
                size="sm"
                :key="JSON.stringify(productOption.values)"
                @input="selectOption($event, productOption.name)"
                :value="getSelectedOption(productOption.name)"
                :options="transformToSelectOptions(productOption.values)"
              ></sib-select>
            </template>
            <!-- show normal option list -->
            <template v-else>
              <span
                class="product__option-choice"
                :style="buildOptionChoiceStyle(productOption.type, option)"
                @click="selectOption(option, productOption.name)"
                :class="
                  buildOptionChoiceClasses(
                    option,
                    productOption.name,
                    productOption.type
                  )
                "
                v-for="option of isOptionTypeName(productOption.name)
                  ? filterOptionTypeValues(productOption.values)
                  : productOption.values"
                :key="option"
              >
                <template
                  v-if="
                    canShowOptionAsIcon && isStyleOption(productOption.type)
                  "
                >
                  <!-- use raw image here because firefox can not load svg inside picture element -->
                  <img
                    :src="getStyleOptionIcon(option)?.value"
                    :alt="option"
                    :width="36"
                    :height="36"
                    fetchpriority="low"
                    loading="lazy"
                  />
                </template>
                <template v-else>
                  {{ option }}
                </template>
              </span>
            </template>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="product__option">
        <h2>
          <sib-skeleton-loading width="10em"></sib-skeleton-loading>
        </h2>
        <div class="product__option-choices">
          <sib-skeleton-loading
            v-for="index in 3"
            :key="index"
            width="3.4em"
            height="3.4em"
          ></sib-skeleton-loading>
        </div>
      </div>
      <div class="product__option">
        <h2>
          <sib-skeleton-loading width="6em"></sib-skeleton-loading>
        </h2>
        <div class="product__option-choices">
          <sib-skeleton-loading
            v-for="index in 3"
            :key="index"
            width="3em"
            height="3em"
            round="circle"
          ></sib-skeleton-loading>
        </div>
      </div>
      <div class="product__option">
        <h2>
          <sib-skeleton-loading width="4em"></sib-skeleton-loading>
        </h2>
        <div class="product__option-choices">
          <sib-skeleton-loading
            v-for="index in 6"
            :key="index"
            width="2.5em"
            height="2.5em"
          ></sib-skeleton-loading>
        </div>
      </div>
    </template>

    <div class="mt-md custom-filed-no-preview">
      <h2>
        {{ customFieldName }}
        <span
          v-show="showLableRequired"
          style="font-size: 14px; color: rgb(255, 0, 0)"
          >Required</span
        >
      </h2>
      <sib-input
        :maxlength="15"
        v-model="customField"
        placeholder="E.g: David, Marie..."
      ></sib-input>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ComputedRef, computed, onMounted, ref, watch } from 'vue'
import useProductStore from '@/store/product'
import useSettingStore from '@/store/setting'
import { SelectedOption, ProductOption, KeyValue } from '@/types'
import {
  cloneDeep,
  OPTION_TYPE_SIZE,
  OPTION_TYPE_COLOR,
  OPTION_TYPE_STYLE,
  NUMBER_OF_OPTIONS_TO_SHOW_DROPDOWN,
  TYPE_OPTIONS,
  QUERY_STRING_NO_CACHE,
} from '@/utils'
import { standardizeOptionValue } from '@/utils/product'
import { useRoute } from 'vue-router'
import EventBus from '@/services/eventbus'

const props = withDefaults(
  defineProps<{ productCombo: any; customFieldName: string }>(),
  {}
)

const emit = defineEmits<{
  (e: 'upsell-variant-selected', value: any): void
  (e: 'upsell-custom-field', value: string): void
}>()

const route = useRoute()

const productStore = useProductStore()
const settingStore = useSettingStore()

const product = ref<any>(null)
const pageSettings = computed(() => settingStore.pages)
const selectedVariant = ref<any>(null)
const customField = ref('')
const showLableRequired = ref(false)

interface StyleOptions {
  productTypeToShowIcon: Array<string>
  variantTypeIconMap: Array<KeyValue>
  productTypeToShowDropdownColorOption: Array<string>
}

const colorOptions: ComputedRef<Array<KeyValue>> = computed(
  () => settingStore.productPage?.colorOptions || []
)

const styleOptions: ComputedRef<StyleOptions> = computed(
  () => settingStore.productPage?.styleOptions || {}
)

interface ProductOptionRecursive {
  name: string
  values: Array<{
    value: string
    childs: ProductOptionRecursive[]
  }>
}

// product options that restrict which option is available when another options is choosen
const productOptions: ComputedRef<Array<ProductOptionRecursive>> = computed(
  () => {
    const options: ProductOptionRecursive[] = []
    if (!product.value?.variants) return options
    product.value.variants.forEach((variant) => {
      if (!variant.selectedOptions || !variant.selectedOptions.length) return
      buildOption(
        options,
        variant.selectedOptions[0],
        variant.selectedOptions.slice(1)
      )
    })
    return options
  }
)

// options that are available for the current variant
const productOptionChoices = computed(() => {
  if (!product.value) return []
  if (!selectedVariant.value?.selectedOptions || !productOptions.value?.length)
    return product.value.options
  const clonedSelectedOptions = cloneDeep(selectedVariant.value.selectedOptions)
  let result: Array<ProductOption> = []
  buildOptionChoices(productOptions.value, result, clonedSelectedOptions)
  return result
})

// can show style options as icons or not
const canShowOptionAsIcon: ComputedRef<boolean> = computed(() => {
  if (
    !product.value ||
    !styleOptions.value ||
    !styleOptions.value.productTypeToShowIcon ||
    !styleOptions.value.variantTypeIconMap
  )
    return false
  const productType = (product.value.productType || '')
    .toLowerCase()
    .replaceAll(' ', '')
  // show style options as icons for ony some product that have product type in the list
  if (!styleOptions.value.productTypeToShowIcon?.includes(productType))
    return false
  if (!productOptionChoices.value?.length) return false
  // show style options as icons only for product that have all style value in the icon list
  for (let i = 0; i < productOptionChoices.value.length; i++) {
    const productOption = productOptionChoices.value[i]
    if (isStyleOption(productOption.type) && productOption.values) {
      for (let j = 0; j < productOption.values.length; j++) {
        const value = productOption.values[j]
        if (!value) return false
        const standardValue = standardizeOptionValue(value)
        const styleOption = styleOptions.value.variantTypeIconMap.find(
          (elm) => elm.key == standardValue
        )
        if (!styleOption) return false
      }
    }
  }
  return true
})

onMounted(async () => {
  EventBus.on('require-field-upsell', () => {
    showLableRequired.value = true
  })
  if (props.productCombo) {
    const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]
    product.value = await productStore.getProductByHandle({
      handle: props.productCombo.handle,
      isForceLoadNewData,
    })
    if (product.value) {
      selectedVariant.value = product.value.variants[0]
    }
  }

  watch(
    () => selectedVariant.value,
    () => {
      emit('upsell-variant-selected', selectedVariant.value)
    },
    {
      immediate: true,
    }
  )
  watch(
    () => customField.value,
    () => {
      showLableRequired.value = false
      emit('upsell-custom-field', customField.value)
    }
  )
})

function buildOptionChoices(
  productOptions: ProductOptionRecursive[],
  optionChoices: Array<ProductOption>,
  selectedOptions: Array<SelectedOption>
) {
  const selectedOption = selectedOptions.shift()
  if (!selectedOption) return

  const productOption = productOptions.find(
    (option) => option.name === selectedOption.name
  )
  if (!productOption) return

  optionChoices.push({
    name: productOption.name,
    values: productOption.values.map((option) => option.value),
    type: selectedOption.type,
  })

  const selectedProductOption = productOption.values.find(
    (option) => option.value === selectedOption.value
  )
  if (!selectedProductOption?.childs?.length) return

  buildOptionChoices(
    selectedProductOption.childs,
    optionChoices,
    selectedOptions
  )
}

function buildOption(
  parentOptions: ProductOptionRecursive[],
  selectedOption: SelectedOption,
  childOptions: SelectedOption[]
): any {
  let option = parentOptions.find((o) => o.name === selectedOption.name)
  if (!option) {
    option = {
      name: selectedOption.name,
      values: [],
    }
    parentOptions.push(option)
  }

  let value = option.values.find((v) => v.value === selectedOption.value)
  if (!value) {
    value = {
      value: selectedOption.value,
      childs: [],
    }
    option.values.push(value)
  }
  if (childOptions.length) {
    return buildOption(value.childs, childOptions[0], childOptions.slice(1))
  }
}

function selectOption(option: string, name?: string) {
  if (!name) return
  if (!product.value?.variants || !selectedVariant.value?.selectedOptions)
    return
  const clonedSelectedOptions = cloneDeep(selectedVariant.value.selectedOptions)
  const selectedIndex = clonedSelectedOptions.findIndex((o) => o.name === name)
  clonedSelectedOptions.splice(selectedIndex, 1, {
    name,
    value: option,
  })
  const clonedSelectedOptionsValue = clonedSelectedOptions
    .map((o) => o.value)
    .join('')
  if (!clonedSelectedOptionsValue) return

  let newSelectedVariant = product.value.variants.find((variant) => {
    return clonedSelectedOptionsValue === variant.selectedOptionsValue
  })

  if (!newSelectedVariant) {
    let filteredVariants = product.value.variants

    for (let i = 0; i < clonedSelectedOptions.length; i++) {
      const prevFilteredVariants = filteredVariants
      filteredVariants = filteredVariants.filter((variant) => {
        if (!variant.selectedOptions || !variant.selectedOptions.length)
          return false
        return (
          variant.selectedOptions[i].value === clonedSelectedOptions[i].value
        )
      })
      if (!filteredVariants.length) {
        filteredVariants = prevFilteredVariants
        break
      }
    }
    newSelectedVariant = filteredVariants[0]
  }
  if (newSelectedVariant) {
    selectedVariant.value = newSelectedVariant
  }
}

function getSelectedOption(name: string = '') {
  if (!selectedVariant.value?.selectedOptions) return ''
  const selectedOption = selectedVariant.value.selectedOptions.find(
    (o) => o.name === name
  )
  if (!selectedOption) return ''
  return selectedOption.value
}

function isOptionActive(option: string, name?: string) {
  if (!option) return false
  return option === getSelectedOption(name)
}

function isSizeOption(optionType: string = '') {
  return optionType === OPTION_TYPE_SIZE
}

function isColorOption(optionType: string = '') {
  return optionType === OPTION_TYPE_COLOR
}

function isStyleOption(optionType: string = '') {
  return optionType === OPTION_TYPE_STYLE
}

function isOptionTypeName(optionTypName: string = '') {
  return optionTypName === TYPE_OPTIONS
}

function buildOptionChoiceClasses(
  optionValue: string = '',
  optionName: string = '',
  optionType: string = ''
) {
  return {
    'product__option-choice--actived': isOptionActive(optionValue, optionName),
    'product__option-choice--small': isSizeOption(optionType),
    'product__option-choice--round': isColorOption(optionType),
    'product__option-choice--icon':
      canShowOptionAsIcon.value && isStyleOption(optionType),
  }
}

function buildOptionChoiceStyle(
  optionType: string = '',
  optionValue: string = ''
) {
  if (!isColorOption(optionType)) return {}
  const standardOptionValue = optionValue.toLowerCase().replace(/\s/g, '-')
  const colorOption = colorOptions.value.find(
    (elm) => elm.key == standardOptionValue
  )
  if (!colorOption) return {}
  if (colorOption.value.startsWith('https')) {
    return {
      backgroundImage: `url(${colorOption.value})`,
    }
  }
  return {
    backgroundColor: colorOption.value,
  }
}

function getStyleOptionIcon(optionValue: string) {
  const standardValue = standardizeOptionValue(optionValue)
  return styleOptions.value.variantTypeIconMap.find(
    (elm) => elm.key == standardValue
  )
}

function transformToSelectOptions(values: string[] = []) {
  return values.map((value) => ({
    value,
    label: value,
  }))
}

function isDropdownColorOption() {
  if (!product.value?.productType) return
  return styleOptions.value.productTypeToShowDropdownColorOption?.includes(
    product.value.productType
  )
}

function filterOptionTypeValues(optionsValues?: string[]) {
  if (!optionsValues) return optionsValues
  let newOptionValues = []

  newOptionValues = optionsValues.filter(
    (optionsValue) => !optionsValue.endsWith('.')
  )

  return newOptionValues
}
</script>

<style lang="scss">
.product__combo-no-preview-options-wrapper {
  .product__option:first-child {
    margin-top: 0;
    h2 {
      margin-top: 0;
    }
  }
  h2 {
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .custom-filed-no-preview {
    input {
      border-color: #d5d5d5;
    }
  }
}
</style>
